"use client";

import { getProgramIcon, getProgramIconColor } from "@/content/programs/icons";
import { PageDecorLayer } from "@/components/decorative/PageDecorLayer";
import { SitePageShell } from "@/components/layout/SitePageShell";
import { ProgramPreviewCard } from "@/components/programs/ProgramPreviewCard";
import { ProgramsParallaxHero } from "@/components/programs/ProgramsParallaxHero";
import { ProgramsSectionHeader } from "@/components/programs/ProgramsSectionHeader";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { ProgramsLightCTA } from "@/components/programs/ProgramsLightCTA";

export interface ProgramSummary {
  slug: string;
  title: string;
  description: string;
  features: string[];
  image: string;
}

interface ProgramsListingViewProps {
  programs: ProgramSummary[];
}

export function ProgramsListingView({ programs }: ProgramsListingViewProps) {
  return (
    <SitePageShell>
      <ProgramsParallaxHero programs={programs} />
      <section className="site-section relative overflow-hidden rounded-t-[2rem] bg-[var(--site-surface-alt)] shadow-[0_-12px_40px_rgba(0,0,0,0.08)] md:rounded-t-[2.5rem]">
        <PageDecorLayer variant="section" palette="site" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 pt-8 md:px-6 md:pt-10">
          <ProgramsSectionHeader
            variant="programs-page"
            eyebrow="All Initiatives"
            title="Explore every program"
            subtitle="Focused initiatives addressing community needs across healthcare, education, animal welfare, and more."
            className="mb-8 md:mb-10"
          />
          <Stagger className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((p) => (
              <StaggerItem key={p.slug}>
                <ProgramPreviewCard
                  slug={p.slug}
                  title={p.title}
                  description={p.description}
                  features={p.features}
                  image={p.image}
                  icon={getProgramIcon(p.slug)}
                  iconColor={getProgramIconColor(p.slug)}
                  className="site-card overflow-hidden rounded-3xl border-[var(--site-border)] bg-[var(--site-surface)]"
                />
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <ProgramsLightCTA />
    </SitePageShell>
  );
}
